<template>
    <div v-if="article" class="article">
        <header class="article__header">
            <h1 class="article__heading">{{ article.title }}</h1>
            <p class="article__lead">
                {{ article.lead }}
                <a v-if="!showMore" v-on:click="showMore = true" class="article__lead__more">
                    <svg class="header__nav__facebook__icon">
                        <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-more"></use>
                    </svg>
                </a>
                <span v-if="showMore">
                    {{ article.issue }}
                </span>
            </p>
        </header>

        <div class="article__image" v-if="article.image">
            <div class="article__video" v-if="article.video && article.video.youTubeId">
                <div v-bind:class="{ article__video__container : true, 'article__video__container--19_10' : article.video.format == '19_10' }">
                    <iframe
                        width="640"
                        height="360"
                        :src="'https://www.youtube.com/embed/' + article.video.youTubeId + '?autoplay=1'"
                        frameborder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowfullscreen
                    >
                    </iframe>
                </div>
            </div>
            <div class="article__image__container" v-else>
                <img :src="article.image.url" :title="article.title" />
            </div>
        </div>

        <div class="article__text">
            <div v-for="(block, index) in article.content" v-bind:key="index" class="article__text__column">
                <h2>{{ block.heading }}</h2>
                <p>{{ block.text }}</p>
            </div>
        </div>

        <ListPosts v-if="posts" :posts="posts" heading="relaterte blogginnlegg:" direction="row"> </ListPosts>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ListPosts from '@/components/ListPosts.vue';

export default {
    name: 'article',
    components: {
        ListPosts,
    },
    data: function () {
        return {
            showMore: false,
        };
    },
    computed: mapState(['site', 'article', 'posts']),
    metaInfo() {
        const title = this.site.metaTitle + (this.article ? ' - ' + (this.article.metaTitle ? this.article.metaTitle : this.article.title) : '');
        const description = this.article ? (this.article.metaDescription ? this.article.metaDescription : this.article.lead) : this.site.metaDescription;
        const ogImage =
            this.article && this.article.ogImage
                ? this.article.ogImage.url
                : this.article && this.article.image
                ? this.article.image.url
                : this.site.ogImage
                ? this.site.ogImage
                : null;
        return {
            title: title,
            meta: [
                { name: 'description', content: description },
                { property: 'og:title', title },
                { property: 'og:site_name', content: this.site.metaTitle },
                { property: 'og:description', content: description },
                { property: 'og:type', content: 'article' },
                { property: 'og:url', content: window.location.href },
                { property: 'og:image', content: ogImage },
            ],
        };
    },
    async created() {
        await this.loadArticles('tiltak/' + this.$route.params.slug);
        if (!this.article) {
            this.$router.push({ path: '/404' });
        } else if (this.article && this.article.id) {
            await this.loadPosts({ relatedTo: this.article.id, limit: 3 });
        }
    },
    methods: {
        ...mapActions(['loadArticles', 'loadPosts']),
    },
};
</script>
<style lang="scss">
@import '@/styles/global';

.article {
    background-color: #eaeaea;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    @include bp($laptop) {
        flex-direction: column;
    }

    &__header {
        width: 100%;
        padding: 40px 25px calc(28.13% - 25px) 25px;
        background-color: #fff;

        @include bp($laptop) {
            padding-top: 80px;
            padding-bottom: 140px;
        }
    }

    &__heading {
        margin-bottom: 25px;
        font-size: 39px;
        line-height: 1.1;

        text-align: center;

        @include bp($laptop) {
            font-size: 60px;
        }
    }

    &__lead {
        margin-bottom: 40px;
        padding: 0 25px;
        font-size: 21px;
        line-height: 1.2;

        text-align: center;
        white-space: pre-line;

        @include bp($laptop) {
            max-width: 800px;
            margin: 0 auto 40px auto;

            font-size: 23px;
        }

        &__more {
            margin: 10px auto;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 30px;
            height: 30px;

            border: 1px solid #333;
            border-radius: 50%;
            color: #333;

            svg {
                width: 18px;
                height: 18px;
            }
        }

        span {
            display: block;
            margin-top: 20px;
        }
    }

    &__video {
        @include bp($laptop) {
            flex-grow: 1;
        }

        &__container {
            width: 100%;
            position: relative;
            height: 0;
            padding-bottom: 56.25%;

            iframe {
                display: block;
                position: absolute;
                left: 0;
                top: 0;
                height: 100%;
                width: 100%;
            }

            &--19_10 {
                padding-bottom: 52.7%;
            }
        }
    }

    &__image {
        position: relative;
        width: 100%;
        margin: 0 25px;
        margin-top: calc(-28.13% + 12px);

        @include bp($laptop) {
            margin-left: auto;
            margin-right: auto;
            max-width: 800px;
            margin-top: -120px;

            display: flex;
        }

        &__container {
            position: relative;
            height: 0;
            padding-bottom: calc(56.25%);
            overflow: hidden;
            max-width: 100%;
            background-color: #acacad;

            @include bp($laptop) {
                height: auto;
                padding-bottom: 0;
            }

            img {
                display: block;
            }

            iframe {
                position: absolute;
                left: 0;
                right: 0;
                top: 0;
                bottom: 0;
            }
        }
    }

    &__text {
        width: 100%;
        margin: 25px;
        padding: 16px;

        background-color: #fff;
        color: #141e28;

        font-size: 15px;

        @include bp($laptop) {
            display: flex;
            width: 100%;
            margin-left: auto;
            margin-right: auto;
            padding-left: 0;
            padding-right: 0;
            max-width: 800px;

            background-color: inherit;
            font-size: 16px;

            &__column {
                width: 33%;
                width: calc(33% + 40px);
                padding: 0 40px;
                text-align: center;

                &:first-child {
                    padding-left: 0;
                    width: 33%;
                }
                &:last-child {
                    padding-right: 0;
                    width: 33%;
                }
            }

            &__column + &__column {
                border-left: 1px solid #000;
            }
        }

        h2 {
            font-family: $body-font-famlily;
            font-size: 22px;

            @include bp($laptop) {
                font-size: 25px;
            }
        }

        p {
            font-size: 15px;

            @include bp($laptop) {
                font-size: 17px;
            }
        }
    }
}
</style>
